/**
 * Cache Manager
 *
 * Handles version-based cache invalidation across the application.
 * When the app version changes (deployment), all caches are automatically cleared.
 * Also provides utilities for detecting new deployments and forcing refreshes.
 */

import { VITE_PACKAGE_VERSION } from "@/constants/env";

const CACHE_VERSION_KEY = "facilityai_cache_version";
const LAST_VERSION_CHECK_KEY = "facilityai_last_version_check";
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Checks if the app version has changed and clears caches if needed
 * Should be called once on app initialization
 * @returns {boolean} True if version changed and caches were cleared
 */
export function initializeCacheManager(): boolean {
	try {
		const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
		const currentVersion = VITE_PACKAGE_VERSION;

		// If version has changed, clear all caches
		if (storedVersion !== currentVersion) {
			console.log(
				`[CacheManager] Version changed from ${storedVersion} to ${currentVersion}. Clearing caches...`,
			);

			// Clear localStorage caches (except auth tokens and user preferences)
			const keysToPreserve = ["firebase:", "user_preferences", "auth_"];
			Object.keys(localStorage).forEach((key) => {
				const shouldPreserve = keysToPreserve.some((prefix) =>
					key.startsWith(prefix),
				);
				if (!shouldPreserve) {
					localStorage.removeItem(key);
				}
			});

			// Clear sessionStorage
			sessionStorage.clear();

			// Clear all module-level caches
			clearAllModuleCaches();

			// Store the new version
			localStorage.setItem(CACHE_VERSION_KEY, currentVersion);

			console.log(
				`[CacheManager] Caches cleared successfully for version ${currentVersion}`,
			);
			return true;
		} else {
			console.log(
				`[CacheManager] Version unchanged (${currentVersion}), using existing caches`,
			);
			return false;
		}
	} catch (error) {
		console.error("[CacheManager] Error during cache initialization:", error);
		return false;
	}
}

/**
 * Clears all module-level caches (dashboard, predspend, assets, etc.)
 * Internal helper function
 */
function clearAllModuleCaches(): void {
	try {
		// Dynamically import and call cache clear functions
		import("@/utils/dashboard-cache")
			.then(({ dashboardCache }) => {
				dashboardCache.clear();
			})
			.catch(() => {});

		import("@/utils/predspend-cache")
			.then(({ predspendCache }) => {
				predspendCache.clear();
			})
			.catch(() => {});

		import("@/layouts/signed-in/views/Admin/SubmissionsTable/children/Search")
			.then(({ clearOrganizedCache }) => {
				clearOrganizedCache();
			})
			.catch(() => {});

		import(
			"@/layouts/signed-in/views/Admin/AssetsTable/childFunctions/fetchData"
		)
			.then(({ clearAssetCache }) => {
				clearAssetCache();
			})
			.catch(() => {});

		import("@/layouts/signed-in/views/Locations/childFunctions/fetchData")
			.then(({ clearLocationCache }) => {
				clearLocationCache();
			})
			.catch(() => {});
	} catch (error) {
		console.error("[CacheManager] Error clearing module caches:", error);
	}
}

/**
 * Manually clears all application caches
 * Useful for debugging or force refresh scenarios
 */
export function clearAllCaches(): void {
	try {
		// Clear localStorage (except auth)
		const keysToPreserve = ["firebase:", "user_preferences", "auth_"];
		Object.keys(localStorage).forEach((key) => {
			const shouldPreserve = keysToPreserve.some((prefix) =>
				key.startsWith(prefix),
			);
			if (!shouldPreserve) {
				localStorage.removeItem(key);
			}
		});

		// Clear sessionStorage
		sessionStorage.clear();

		// Clear all module-level caches
		clearAllModuleCaches();

		console.log("[CacheManager] All caches cleared manually");
	} catch (error) {
		console.error("[CacheManager] Error clearing caches:", error);
	}
}

/**
 * Gets the current cache version
 */
export function getCacheVersion(): string {
	return localStorage.getItem(CACHE_VERSION_KEY) || "unknown";
}

/**
 * Force logout all users by incrementing the stored version
 * This will cause cache invalidation on next page load
 */
export function forceGlobalCacheInvalidation(): void {
	const currentVersion = VITE_PACKAGE_VERSION;
	const newVersion = `${currentVersion}-force-${Date.now()}`;
	localStorage.setItem(CACHE_VERSION_KEY, newVersion);
	console.log(
		"[CacheManager] Forced cache invalidation. Users will be cleared on next visit.",
	);
}

/**
 * Check if a new version is available by fetching index.html and checking meta tags
 * This detects midday deployments
 * @returns {Promise<boolean>} True if new version detected
 */
export async function checkForNewVersion(): Promise<boolean> {
	try {
		// Skip version checking in development mode (localhost)
		if (
			window.location.hostname === "localhost" ||
			window.location.hostname === "127.0.0.1"
		) {
			return false;
		}

		// Don't check too frequently
		const lastCheck = localStorage.getItem(LAST_VERSION_CHECK_KEY);
		const now = Date.now();
		if (lastCheck && now - parseInt(lastCheck) < VERSION_CHECK_INTERVAL) {
			return false;
		}

		// Update last check time
		localStorage.setItem(LAST_VERSION_CHECK_KEY, now.toString());

		// Fetch index.html with cache-busting to check for new version
		const response = await fetch(`/?t=${now}`, {
			cache: "no-cache",
			headers: {
				"Cache-Control": "no-cache, no-store, must-revalidate",
				Pragma: "no-cache",
			},
		});

		if (!response.ok) {
			console.warn("[CacheManager] Failed to check version");
			return false;
		}

		const html = await response.text();

		// Extract version from meta tag in the fetched HTML
		const versionMatch = html.match(
			/<meta\s+name="app-version"\s+content="([^"]+)"\s*\/?>/,
		);
		const buildTimeMatch = html.match(
			/<meta\s+name="build-time"\s+content="([^"]+)"\s*\/?>/,
		);

		if (!versionMatch && !buildTimeMatch) {
			console.warn("[CacheManager] Could not find version meta tag in HTML");
			return false;
		}

		const serverVersion = versionMatch?.[1];
		const serverBuildTime = buildTimeMatch?.[1];
		const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);

		// Check if either version or build time has changed
		if (storedVersion && serverVersion && serverVersion !== storedVersion) {
			console.log(
				`[CacheManager] New version detected: ${serverVersion} (current: ${storedVersion})`,
			);
			return true;
		}

		// Also check build time as a fallback (in case version didn't increment)
		if (serverBuildTime) {
			const storedBuildTime = localStorage.getItem("facilityai_build_time");
			if (storedBuildTime && serverBuildTime !== storedBuildTime) {
				console.log(
					`[CacheManager] New build detected: ${serverBuildTime} (current: ${storedBuildTime})`,
				);
				localStorage.setItem("facilityai_build_time", serverBuildTime);
				return true;
			}
			if (!storedBuildTime) {
				localStorage.setItem("facilityai_build_time", serverBuildTime);
			}
		}

		return false;
	} catch (error) {
		console.error("[CacheManager] Error checking for new version:", error);
		return false;
	}
}

/**
 * Force reload the application with cache bypass
 * This ensures the latest version is loaded
 */
export function forceReload(): void {
	console.log("[CacheManager] Forcing application reload...");

	// Clear all caches before reload
	clearAllCaches();

	// Clear service worker caches if available
	if ("serviceWorker" in navigator && "caches" in window) {
		caches
			.keys()
			.then((cacheNames) => {
				return Promise.all(
					cacheNames.map((cacheName) => caches.delete(cacheName)),
				);
			})
			.then(() => {
				// Unregister all service workers
				if ("serviceWorker" in navigator) {
					navigator.serviceWorker.getRegistrations().then((registrations) => {
						for (const registration of registrations) {
							registration.unregister();
						}
					});
				}
				// Hard reload bypassing cache
				window.location.reload();
			})
			.catch(() => {
				window.location.reload();
			});
	} else {
		window.location.reload();
	}
}

/**
 * Setup automatic version checking on window focus
 * This detects deployments when users return to the tab
 */
export function setupVersionMonitoring(): void {
	if (typeof window === "undefined") return;

	let hasNotifiedUser = false;

	// Check for new version when window gains focus
	const handleFocus = async () => {
		if (hasNotifiedUser) return; // Don't spam the user

		const newVersionAvailable = await checkForNewVersion();
		if (newVersionAvailable) {
			hasNotifiedUser = true;
			// Notify user and offer to reload
			const shouldReload = window.confirm(
				"A new version of FacilityAI is available. The page will reload to get the latest updates.\n\nClick OK to reload now, or Cancel to continue (you may experience issues).",
			);
			if (shouldReload) {
				forceReload();
			} else {
				// User declined, but mark for reload on next navigation
				sessionStorage.setItem("pending_version_update", "true");
			}
		}
	};

	window.addEventListener("focus", handleFocus);

	// Also check periodically if the tab is visible
	const intervalId = setInterval(async () => {
		if (!document.hidden && !hasNotifiedUser) {
			const newVersionAvailable = await checkForNewVersion();
			if (newVersionAvailable) {
				hasNotifiedUser = true;
				clearInterval(intervalId);
				const shouldReload = window.confirm(
					"A new version of FacilityAI is available. The page will reload to get the latest updates.\n\nClick OK to reload now, or Cancel to continue (you may experience issues).",
				);
				if (shouldReload) {
					forceReload();
				} else {
					// User declined, but mark for reload on next navigation
					sessionStorage.setItem("pending_version_update", "true");
				}
			}
		}
	}, VERSION_CHECK_INTERVAL);

	// Cleanup on page unload
	window.addEventListener("beforeunload", () => {
		clearInterval(intervalId);
		window.removeEventListener("focus", handleFocus);
	});

	// Check if there's a pending update on page load
	if (sessionStorage.getItem("pending_version_update") === "true") {
		console.log(
			"[CacheManager] Pending version update detected, forcing reload...",
		);
		sessionStorage.removeItem("pending_version_update");
		// Give a brief moment for the page to load before reloading
		setTimeout(() => {
			forceReload();
		}, 1000);
	}
}

// Expose cache management functions to window for debugging
if (typeof window !== "undefined") {
	(window as any).FacilityAI = {
		...(window as any).FacilityAI,
		clearAllCaches,
		getCacheVersion,
		forceGlobalCacheInvalidation,
		checkForNewVersion,
		forceReload,
	};
}
